// HolographicSwatch.tsx
// Exploits LCD viewing angle physics + 3D parallax for true angle-dependent color shift

import React from 'react';

interface HolographicSwatchProps {
  color: string;
  className?: string;
  children?: React.ReactNode;
}

export const HolographicSwatch: React.FC<HolographicSwatchProps> = ({ color, className = '', children }) => {
  const hexToRgb = (hex: string) => {
    const clean = hex.replace('#', '');
    return {
      r: parseInt(clean.substr(0, 2), 16),
      g: parseInt(clean.substr(2, 2), 16),
      b: parseInt(clean.substr(4, 2), 16)
    };
  };

  const rgb = hexToRgb(color);
  
  // Layer strategy: Exploit LCD crystal twist + 3D depth
  // TN/IPS panels shift blue most (±15° causes 30% shift), red least (±15° = 5% shift)
  const layers = [
    // Base - original color, no shift
    { c: color, o: 1, z: 0, b: 'normal' as const },
    
    // Cyan (B+G dominant) - maximum LCD angle sensitivity
    { 
      c: `rgb(${rgb.r * 0.2},${Math.min(255, rgb.g * 1.3)},${Math.min(255, rgb.b * 1.5)})`,
      o: 0.5, z: 3, b: 'screen' as const 
    },
    
    // Magenta (R+B) - medium sensitivity, red anchors
    { 
      c: `rgb(${Math.min(255, rgb.r * 1.4)},${rgb.g * 0.3},${Math.min(255, rgb.b * 1.3)})`,
      o: 0.4, z: 6, b: 'overlay' as const 
    },
    
    // Yellow (R+G) - minimal shift, stable at angles
    { 
      c: `rgb(${Math.min(255, rgb.r * 1.2)},${Math.min(255, rgb.g * 1.2)},${rgb.b * 0.4})`,
      o: 0.35, z: 9, b: 'color-dodge' as const 
    },
    
    // Spectral blue - exploits maximum blue shift
    { 
      c: `rgb(${rgb.r * 0.5},${rgb.g * 0.6},255)`,
      o: 0.3, z: 12, b: 'screen' as const 
    }
  ];

  return (
    <div 
      className={`relative ${className}`}
      style={{
        perspective: '800px',
        transformStyle: 'preserve-3d',
        isolation: 'isolate'
      }}
    >
      {layers.map((l, i) => (
        <div
          key={i}
          style={{
            position: i === 0 ? 'relative' : 'absolute',
            inset: 0,
            backgroundColor: l.c,
            opacity: l.o,
            transform: `translateZ(${l.z}px) rotateX(0.5deg) rotateY(0.5deg)`,
            mixBlendMode: l.b,
            transformStyle: 'preserve-3d',
            backfaceVisibility: 'visible',
            willChange: 'transform'
          }}
        />
      ))}
      
      {/* RGB subpixel interference pattern */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            linear-gradient(90deg, 
              rgba(255,0,0,0.04) 0%, transparent 33%,
              rgba(0,255,0,0.04) 33%, transparent 66%,
              rgba(0,0,255,0.04) 66%, transparent 100%
            )
          `,
          backgroundSize: '3px 100%',
          mixBlendMode: 'screen',
          pointerEvents: 'none',
          transform: 'translateZ(15px)',
          transformStyle: 'preserve-3d'
        }}
      />
      
      {/* Content wrapper */}
      {children && (
        <div style={{ position: 'relative', zIndex: 10, transform: 'translateZ(20px)', transformStyle: 'preserve-3d' }}>
          {children}
        </div>
      )}
    </div>
  );
};